import { EditOutlined, FileTextOutlined, FormOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useRequest } from 'ahooks';
import { Button, Drawer, Form, Input, Space, Spin } from 'antd';
import useNotification from 'antd/es/notification/useNotification';
import ConversationTopBar from 'components/chats/ConversationTopBar';
import PatientDocumentsList from 'components/lists/PatientDocumentsList';
import PatientProfileModal from 'components/modals/PatientProfileModal';
import QuestionnaireResponseViewer from 'components/questionnaire/QuestionnaireResponseViewer';
import useSelectConversations from 'hooks/useSelectConversations';
import posthog from 'posthog-js';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ApiDoctorConversationsManager from 'services/api/ApiDoctorConversationsManager';
import ApiPatientDocumentsManager from 'services/api/ApiPatientDocumentsManager';
import ApiQuestionnaireManager from 'services/api/ApiQuestionnaireManager';
import ApiSymptomTrackingManager from 'services/api/ApiSymptomTrackingManager';
import { ConversationWithMessagesDTO } from 'services/api/models/ConversationDTO';
import { QuestionnaireResponseDTO } from 'services/api/models/QuestionnaireDTO';
import { conversationActions } from 'store/conversations';
import { colors } from 'design';

const { TextArea } = Input;

const useStyles = createUseStyles({
  pageContainer: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: colors.surface.page,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    padding: 16,
    overflow: 'scroll',
  },
  section: {
    maxWidth: 600,
    width: '100%',
    borderRadius: 8,
    display: 'flex',
    flexDirection: 'column',
  },
  documentsList: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
    marginTop: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 600,
    marginTop: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  sectionTitleSubtitleText: {
    fontSize: 13,
    color: colors.text.disabled,
    fontWeight: 'normal',
    marginLeft: 4,
    marginTop: 4,
    '& .anticon': {
      marginRight: 4,
    },
  },
  summaryForm: {
    flex: 1,
    gap: 16,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    '& .ant-form-item': {
      marginBottom: 16,
    },
    '& textarea': {
      width: '100%',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: 20,
    color: colors.text.placeholder,
  },
  questionnaireRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: `1px solid ${colors.brand.accentSubtle}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  questionnaireLabel: {
    fontWeight: 500,
  },
  questionnairePlaceholder: {
    color: colors.text.tertiary,
    fontSize: 13,
    fontStyle: 'italic',
  },
});

export default function CaregiverDossierPage() {
  const styles = useStyles();
  const { t } = useTranslation();
  const [api, context] = useNotification();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { conversationId } = useParams<{ conversationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { conversationMetadata, conversationSummaryDraft } = useSelectConversations();

  const [form] = Form.useForm();
  const [displayedSummary, setDisplayedSummary] = useState<string>('');
  const summaryValue = Form.useWatch('summary', form);

  // Questionnaire state
  const [openOnboardingModal, setOpenOnboardingModal] = useState(false);
  const [isMedicalDrawerOpen, setIsMedicalDrawerOpen] = useState(false);
  const [medicalFetchEnabled, setMedicalFetchEnabled] = useState(false);

  // we need this ref to access the summary value on unmount
  const summaryValueRef = useRef(conversationSummaryDraft || '');
  useEffect(() => {
    if (summaryValue !== undefined) summaryValueRef.current = summaryValue;
  }, [summaryValue]);

  // The conversations cache only holds the caregiver's own conversations, so it misses
  // e.g. another caregiver's conversation opened from the patient's history. Fall back to
  // the conversation fetched below so the dossier always resolves the right patient.
  const [loadedConversation, setLoadedConversation] = useState<ConversationWithMessagesDTO>();
  const conversation =
    conversationMetadata?.conversation ?? (loadedConversation?.id === conversationId ? loadedConversation : undefined);
  const patient = conversation?.patient;
  const questionnaireResponseId = conversation?.questionnaire_response_id;

  // Check if onboarding questionnaire exists
  const { data: onboardingSurveys, loading: loadingOnboarding } = useRequest(
    async () => {
      if (!patient?.id) return { hasOnboarding: false };
      const [v1, v2] = await Promise.allSettled([
        ApiSymptomTrackingManager.getOnboardingSurvey(patient.id),
        ApiSymptomTrackingManager.getOnboardingSurveyV2(patient.id),
      ]);
      return { hasOnboarding: v1.status === 'fulfilled' || v2.status === 'fulfilled' };
    },
    { refreshDeps: [patient?.id] },
  );

  const { data: medicalQuestionnaire, isFetching: loadingMedical } = useQuery<QuestionnaireResponseDTO | null>({
    queryKey: ['questionnaire', 'responseForPatient', questionnaireResponseId],
    queryFn: () =>
      questionnaireResponseId
        ? ApiQuestionnaireManager.getResponseForPatient(questionnaireResponseId).catch(() => null)
        : Promise.resolve(null),
    staleTime: 1000 * 60 * 5,
    enabled: medicalFetchEnabled && !!questionnaireResponseId,
  });
  // Use the schema that was embedded in the response — this is the version the
  // patient actually answered, not the current active schema.
  const medicalSchema = medicalQuestionnaire?.schema ?? null;

  function openMedicalDrawer() {
    setMedicalFetchEnabled(true);
    setIsMedicalDrawerOpen(true);
    if (searchParams.has('openMedical')) {
      setSearchParams(
        params => {
          params.delete('openMedical');
          return params;
        },
        { replace: true },
      );
    }
  }

  // Auto-open medical questionnaire when ?openMedical=1 is in the URL
  useEffect(() => {
    if (searchParams.get('openMedical') === '1' && questionnaireResponseId) {
      openMedicalDrawer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionnaireResponseId]);

  const {
    data: documents,
    loading: loadingDocuments,
    refresh: refreshDocuments,
  } = useRequest(() => ApiPatientDocumentsManager.listForCaregiver({ patientId: patient?.id }), {
    // Without a patient id the endpoint returns the documents of every patient shared
    // with this caregiver, so wait until the patient is known.
    ready: !!patient?.id,
    refreshDeps: [patient?.id],
    onError: () => {
      api.error({ message: t('documents.errors.loadError') });
    },
  });

  const { loading: loadingSummary, run: loadSummary } = useRequest(
    () => ApiDoctorConversationsManager.getDoctorConversationMessageList({ conversationId: conversationId! }),
    {
      onSuccess: result => {
        setLoadedConversation(result);
        const summary = result.summary || '';
        setDisplayedSummary(summary);
        // Use draft if available, otherwise use the loaded summary
        const initialValue = conversationSummaryDraft || summary;
        form.setFieldsValue({ summary: initialValue });
        summaryValueRef.current = initialValue;
      },
      onError: () => {
        api.error({ message: t('chats.summary.summaryLoadError') });
      },
    },
  );

  function saveDraft() {
    if (conversationId) {
      dispatch(conversationActions.setConversationSummaryDraft({ conversationId, summary: summaryValueRef.current }));
    }
  }

  // Load summary and documents on mount
  React.useEffect(() => {
    if (conversationId) {
      loadSummary();
    }
    return () => {
      // Save draft when component unmounts
      saveDraft();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, loadSummary]);

  // Update form when draft changes (e.g., when navigating between conversations)
  useEffect(() => {
    if (conversationSummaryDraft !== undefined) {
      const initialValue = conversationSummaryDraft || displayedSummary;
      form.setFieldsValue({ summary: initialValue });
      summaryValueRef.current = initialValue;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, conversationSummaryDraft]);

  const { loading: savingSummary, run: saveSummary } = useRequest(
    (summary: string) => ApiDoctorConversationsManager.updateConversationSummary(conversationId!, summary),
    {
      manual: true,
      onSuccess: (_, [summary]) => {
        setDisplayedSummary(summary || '');
        summaryValueRef.current = summary || '';
        // Clear draft when summary is successfully saved
        if (conversationId) {
          dispatch(conversationActions.setConversationSummaryDraft({ conversationId, summary: '' }));
        }
        api.success({ message: t('chats.summary.summarySaved') });
      },
      onError: () => {
        api.error({ message: t('chats.summary.summarySaveError') });
      },
    },
  );

  const onSummarySubmit = (values: { summary: string }) => {
    saveSummary(values.summary);
    posthog.capture('patient_file.summary_updated');
  };

  return (
    <div className={styles.pageContainer}>
      {context}
      <ConversationTopBar
        showBackButton
        avatar={patient?.photo_url}
        name={patient?.display_name}
        patientId={patient?.id}
        onBackButtonClick={() => {
          saveDraft();
          navigate(-1);
        }}
      />

      {openOnboardingModal && patient?.id && patient?.display_name && (
        <PatientProfileModal
          patientId={patient.id}
          patientName={patient.display_name}
          open
          onClose={() => setOpenOnboardingModal(false)}
        />
      )}

      <Drawer
        title={t('medicalProfile.caregiver.questionnaireModalTitle')}
        open={isMedicalDrawerOpen}
        onClose={() => setIsMedicalDrawerOpen(false)}
        placement="bottom"
        size="85%"
      >
        {loadingMedical ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spin />
          </div>
        ) : medicalQuestionnaire && medicalSchema ? (
          <QuestionnaireResponseViewer
            response={medicalQuestionnaire}
            schema={medicalSchema}
            responseId={medicalQuestionnaire.id}
          />
        ) : (
          <p>{t('medicalProfile.caregiver.noResponse')}</p>
        )}
      </Drawer>

      <div className={styles.container}>
        {/* Questionnaire Section */}
        <div className={styles.section} style={{ marginBottom: 8 }}>
          <div className={styles.sectionTitle}>
            <FormOutlined />
            {t('chats.dossier.questionnaires')}
          </div>
          <div style={{ marginTop: 8 }}>
            <div className={styles.questionnaireRow}>
              <span className={styles.questionnaireLabel}>{t('chats.dossier.onboardingQuestionnaire')}</span>
              {loadingOnboarding ? (
                <Spin size="small" />
              ) : onboardingSurveys?.hasOnboarding ? (
                <Button size="medium" onClick={() => setOpenOnboardingModal(true)}>
                  {t('chats.dossier.viewQuestionnaire')}
                </Button>
              ) : (
                <span className={styles.questionnairePlaceholder}>{t('chats.dossier.noOnboardingQuestionnaire')}</span>
              )}
            </div>
            <div className={styles.questionnaireRow}>
              <span className={styles.questionnaireLabel}>{t('chats.dossier.medicalQuestionnaire')}</span>
              {questionnaireResponseId ? (
                <Button size="medium" loading={loadingMedical} onClick={openMedicalDrawer}>
                  {t('chats.dossier.viewQuestionnaire')}
                </Button>
              ) : (
                <span className={styles.questionnairePlaceholder}>{t('chats.dossier.noMedicalQuestionnaire')}</span>
              )}
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <EditOutlined />
            {t('chats.summary.title')}
          </div>
          <p className={styles.sectionTitleSubtitleText}>{t('chats.summary.subtitle')}</p>
          <Form
            form={form}
            onFinish={onSummarySubmit}
            className={styles.summaryForm}
            initialValues={{ summary: conversationSummaryDraft || '' }}
          >
            <Form.Item noStyle name="summary">
              <TextArea
                placeholder={t('chats.summary.summaryPlaceholder')}
                autoSize={{ minRows: 3, maxRows: 10 }}
                disabled={loadingSummary || savingSummary}
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={savingSummary}
                  disabled={!summaryValue || summaryValue === displayedSummary}
                >
                  {t('common.save')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </div>

        {/* Documents Section */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <FileTextOutlined />
            {t('chats.dossier.documents')}
          </div>
          <div className={styles.documentsList}>
            {(loadingDocuments || !patient?.id) && documents === undefined ? (
              <div className={styles.emptyState}>{t('common.loading')}</div>
            ) : !documents || documents.length === 0 ? (
              <div className={styles.emptyState}>{t('chats.dossier.noDocuments')}</div>
            ) : (
              <PatientDocumentsList documents={documents} isCaregiver={true} onRefresh={refreshDocuments} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
